import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import { analyticsConfigured } from '../../config/posthog';
import { onOpenConsentSettings, readConsent, writeConsent, type ConsentChoice } from '../../config/consent';
import { COOKIE_NOTICE_URL } from '../../config/legal';

// The scanned product and shared-catalogue pages are always dark, whatever
// the app theme says: the banner takes their look there.
const ALWAYS_DARK = /^\/(p|product|s)\//;

// Equal weight for both answers: same size, same colour (privacy decision
// 2026-10-31: "Accept analytics" and "Refuse" equally prominent).
const answerClass =
  'h-10 rounded-xl px-4 text-sm font-semibold text-white bg-william-600 hover:bg-william-700 dark:bg-william-300 dark:text-night-950 dark:hover:bg-william-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-william-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-night-900 transition-colors';

/**
 * The analytics consent banner. Shown until the visitor answers (the answer
 * lasts six months, shared with basiqr.com), and again from any "Cookie
 * settings" link. Not modal: the page stays usable while it is up.
 */
const CookieConsent: React.FC = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(() => analyticsConfigured() && readConsent() === null);
  const [reopened, setReopened] = useState<ConsentChoice | null>(null);
  const panelRef = useRef<HTMLElement>(null);

  useEffect(
    () =>
      onOpenConsentSettings(() => {
        setReopened(readConsent());
        setOpen(true);
        // After the panel renders: settings were asked for, so take the focus there.
        requestAnimationFrame(() => panelRef.current?.focus());
      }),
    []
  );

  if (!open || !analyticsConfigured()) return null;

  const answer = (choice: ConsentChoice) => {
    writeConsent(choice);
    setOpen(false);
  };

  return (
    <div className={ALWAYS_DARK.test(pathname) ? 'dark' : undefined}>
      <section
        ref={panelRef}
        tabIndex={-1}
        role="region"
        aria-label="Cookie choice"
        data-testid="cookie-consent"
        className="fixed z-[100] inset-x-3 bottom-3 sm:inset-x-auto sm:right-5 sm:bottom-5 sm:w-[400px] rounded-2xl p-5 font-sans text-gray-700 bg-white/95 border border-gray-200/80 shadow-[0_18px_44px_-18px_rgb(18_38_42/0.45)] backdrop-blur-md dark:text-gray-300 dark:bg-night-900/95 dark:border-white/10 dark:shadow-[0_18px_44px_-18px_rgb(0_0_0/0.8)] focus:outline-none"
      >
        <h2 className="flex items-center gap-2 font-display text-[15px] font-semibold text-gray-900 dark:text-white">
          <Cookie size={16} className="text-william-600 dark:text-william-300" aria-hidden="true" />
          Analytics cookies
        </h2>
        <p className="mt-2 text-[13.5px] leading-relaxed">
          We’d like to use PostHog analytics to see how BasiQR is used and what to improve, including
          recordings of visits with anything you type hidden. Nothing is collected unless you accept.{' '}
          <a
            href={COOKIE_NOTICE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-william-700 underline underline-offset-2 hover:text-william-900 dark:text-william-200 dark:hover:text-white"
          >
            Cookie notice
          </a>
        </p>
        {reopened && (
          <p className="mt-2 text-[13px] text-gray-500 dark:text-gray-400">
            Your current choice: {reopened === 'granted' ? 'analytics accepted' : 'analytics refused'}.
          </p>
        )}
        <div className="mt-4 grid grid-cols-2 gap-2.5">
          <button type="button" onClick={() => answer('denied')} className={answerClass}>
            Refuse
          </button>
          <button type="button" onClick={() => answer('granted')} className={answerClass}>
            Accept analytics
          </button>
        </div>
      </section>
    </div>
  );
};

export default CookieConsent;
